/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import { FC, useState, useEffect, useRef } from "react";
import { AwvMapOverlayProps } from "./AwvMapOverlay";
import { useGoogleMap } from "@react-google-maps/api";
import { Fab, Dialog, Box, Grid, useTheme } from "@mui/material";
import { createAwvIconDataUrl } from "./AwvIcon";
import { AwvInfo, AwvInfoConditionEnum } from "client";
import { ReactComponent as HighLightIcon } from "assets/icon_highlight.svg";

interface Props extends AwvMapOverlayProps {}

interface MagnifyProps {
  open: boolean;
  onClose: () => void;
  offlineAwvInfo: AwvInfo[];
  onAwvSelect?: (awvInfo: AwvInfo) => void;
  selected?: string[];
}

const MagnifyOffline: FC<MagnifyProps> = ({
  open,
  onClose,
  offlineAwvInfo,
  onAwvSelect,
  selected,
}) => {
  const theme = useTheme();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: "400px",
          width: "400px",
          borderRadius: "50%",
          bgcolor: theme.palette.grey[300],
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        },
      }}
    >
      <Grid container sx={{ width: 200 }}>
        {offlineAwvInfo.map((awvInfo) => (
          <Grid key={`offlinegrid-${awvInfo.frameTypeAndNumber}`} item xs={6}>
            <Box
              sx={{
                position: "relative",
                overflow: "visible",
              }}
            >
              <img
                key={`offlineimg-${awvInfo.frameTypeAndNumber}`}
                src={createAwvIconDataUrl(awvInfo)}
                alt=""
                onClick={() => {
                  onAwvSelect?.(awvInfo);
                }}
              />
              {selected?.some((i) => i === awvInfo.frameTypeAndNumber) && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -30,
                    left: -30,
                    zIndex: theme.zIndex.modal + 2,
                  }}
                >
                  <HighLightIcon />
                </Box>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>
    </Dialog>
  );
};

export const OfflineAwvMapOverlay: FC<Props> = ({
  awvInfos,
  onAwvSelect,
  selected,
}) => {
  const map = useGoogleMap();
  const ref = useRef<HTMLButtonElement>(null);
  const offlineAwvs = awvInfos?.awvInfos?.filter(
    (awvInfo) => awvInfo.condition === AwvInfoConditionEnum.Offline
  );
  offlineAwvs?.sort((a, b) => ((a.nameId ?? "") > (b.nameId ?? "") ? 1 : -1));
  const [magnify, setMagnify] = useState(false);
  useEffect(() => {
    if (map) {
      const controls = map.controls[google.maps.ControlPosition.LEFT_TOP];
      const idx = controls
        .getArray()
        .findIndex((e) => e.id === "map-control-offline-icon");
      if (ref.current) {
        if (idx === -1) {
          // not found.
          controls.push(ref.current);
        } else {
          // replace
          controls.setAt(idx, ref.current);
        }
      } else {
        if (idx !== -1) {
          controls.removeAt(idx);
        }
      }
    }
  }, [map, awvInfos]);
  const isSelected = offlineAwvs?.some((i) =>
    selected?.some((s) => s === i.frameTypeAndNumber)
  );
  // (offlineAwvs?.findIndex(
  //   (awv) => awv.frameTypeAndNumber === selected?.frameTypeAndNumber
  // ) ?? -1) >= 0;
  if (offlineAwvs && offlineAwvs.length > 0) {
    const offlineIconUrl = createAwvIconDataUrl(offlineAwvs[0]);
    return (
      <>
        <Fab
          id="map-control-offline-icon"
          ref={ref}
          onClick={() => {
            // show magnify when greater than 1.
            if (offlineAwvs.length > 1) {
              setMagnify(true);
            } else {
              onAwvSelect?.(offlineAwvs[0]);
            }
          }}
          color={isSelected ? "primary" : "inherit"}
          sx={{ width: 120, height: 120 }}
        >
          <img src={offlineIconUrl} alt="" />
        </Fab>
        {magnify && (
          <MagnifyOffline
            open={magnify}
            onClose={() => {
              setMagnify(false);
            }}
            offlineAwvInfo={offlineAwvs}
            onAwvSelect={onAwvSelect}
            selected={selected}
          />
        )}
      </>
    );
  } else {
    return null;
  }
};
